import { useTimer } from '../';
import { ApplicationFormStatus, useApplicationStatusQuery } from '../../graphql';
import { ApplicationSubmissionStatus } from '../../pages/ApplicationStatus/types';
import { useApplicationData } from '../../provider';
import { MAX_SYNC_STATUS_WAIT, getApplicantTypeAndTrackingIdFromUrl, instantApprovalProducts } from '../../utils';
import { getSubmissionStatus } from './helpers';

export const useGetApplicationSubmissionStatus = (): {
  status: ApplicationSubmissionStatus;
  time?: number;
} => {
  const {
    SubmitApplication: submitApplication,
    HouseholdDetails: householdDetails,
    quote,
  } = useApplicationData(['SubmitApplication', 'HouseholdDetails', 'quote']);

  const formSubmitted = submitApplication?.status === ApplicationFormStatus.Submitted;
  const hasJointApplicant = householdDetails?.hasJointApplicant;
  const productIsInstantApproval = instantApprovalProducts.includes(quote?.financialProduct?.id ?? '');

  //get application status
  const { trackingId } = getApplicantTypeAndTrackingIdFromUrl();
  const { time, clearTimer } = useTimer({ max: MAX_SYNC_STATUS_WAIT, interval: 1000, direction: 'asc' });
  const { data: application } = useApplicationStatusQuery(
    { applicationId: trackingId },
    {
      // refetch every second if application status is submitted
      refetchInterval: data => {
        const status = data?.application?.status;
        if (!status || (status === ApplicationFormStatus.Submitted && time < MAX_SYNC_STATUS_WAIT)) {
          return 5000;
        }
        return false;
      },

      useErrorBoundary: true,
      // only enable query if product is instant approval and form is submitted without joint applicant
      // and time is less than MAX_SYNC_STATUS_WAIT
      enabled: productIsInstantApproval && formSubmitted && !hasJointApplicant && time <= MAX_SYNC_STATUS_WAIT,
    },
  );

  const applicationStatus = application?.application?.status;
  const submissionStatus = getSubmissionStatus(productIsInstantApproval, !!hasJointApplicant, time, applicationStatus);

  if (submissionStatus !== ApplicationSubmissionStatus.FETCHING) {
    clearTimer();
  }

  return {
    status: submissionStatus,
    time,
  };
};
